// Created on 2023-08-03
// Created by Vitaut Tryputsin
//
// Copyright (c) 2023 OPEN CASCADE SAS
//
// This file is part of commercial software by OPEN CASCADE SAS,
// furnished in accordance with the terms and conditions of the contract
// and with the inclusion of this copyright notice.
// This file or any part thereof may not be provided or otherwise
// made available to any third party.
//
// No ownership title to the software is transferred hereby.
//
// OPEN CASCADE SAS makes no representation or warranties with respect to the
// performance of this software, and specifically disclaims any responsibility
// for any damages, special or consequential, connected with its use.
import { Vec3Tools } from "../geometry/vec3-tools.js";
/**
 * Represents Camera manager.
 * See also [[OccViewerModule.camera]] property.
 */
export class Camera {
    /** Main constructor. */
    constructor(theViewer, theViewId = -1) {
        this._myViewId = theViewId;
        this._myViewMgr = theViewer.viewManager;
    }
    /** Orbit acceleration coefficient. */
    get orbitAcceleration() { return this._myViewMgr.getOrbitAcceleration(); }
    /** Orbit acceleration coefficient. */
    set orbitAcceleration(theAccel) { this._myViewMgr.setOrbitAcceleration(theAccel); }
    /** Max camera distance multiplier; when it equals 0 or negative then disable the max zoom limit. */
    get maxCamDistanceMultiplier() {
        return this._myViewMgr.getMaxCamDistanceMultiplier(this._myViewId);
    }
    get cameraParameters() {
        return this._myViewMgr.getViewPoint(this._myViewId);
    }
    set cameraParameters(theParameters) {
        this._myViewMgr.setViewPoint(this._myViewId, theParameters);
    }
    /** Set max camera distance multiplier. */
    set maxCamDistanceMultiplier(theMax) {
        this._myViewMgr.setMaxCamDistanceMultiplier(this._myViewId, theMax);
    }
    /** Fit selected or fit all visible objects into view. */
    fitAllAuto(theIsAuto) {
        this._myViewMgr.fitAllAuto(this._myViewId, theIsAuto);
    }
    /**
     * Fit given objects into view.
     * @param {string} theNames names of objects to fit or special cases
     *                          (`"*"` -> fitAll)
     *                          (`""`  -> fitAuto (if something selected - fit selected or fit all))
     * @param {boolean} theToUpdate viewer update flag
     */
    fitObjects(theNames, theToUpdate) {
        this._myViewMgr.fitObjects(this._myViewId, theNames, theToUpdate);
    }
    /**
     * Fit all/selected objects into view.
     * @param {boolean} theAuto fit selected objects (TRUE) or all objects (FALSE)
     * @param {boolean} theToUpdate viewer update flag
     */
    fitAllObjects(theAuto, theToUpdate) {
        this._myViewMgr.fitAllObjects(this._myViewId, theAuto, theToUpdate);
    }
    /** Fit box into view. */
    fitMinMax(theMin, theMax) {
        this._myViewMgr.fitMinMax(this._myViewId, theMin, theMax);
    }
    /**
      * Set camera view orientation.
      * @param theOrientation viewer orientation
      */
    setOrientation(theOrientation) {
        this._myViewMgr.setOrientation(this._myViewId, theOrientation);
    }
    /** Return current camera view point parameters. */
    get viewPoint() { return this._myViewMgr.getViewPoint(this._myViewId); }
    /** Set camera view point parameters. */
    set viewPoint(theParams) {
        this._myViewMgr.setViewPoint(this._myViewId, theParams);
    }
    /** Set camera direction to look at image. */
    lookAtImageAxes(theDirection, theXDirection, theLocation) {
        let aDir = theDirection;
        let aDirX = theXDirection;
        let anUp = Vec3Tools.cross(aDir, aDirX);
        let aViewPnt = this.viewPoint;
        aDir = Vec3Tools.reverse(aDir);
        aViewPnt.eye = Vec3Tools.add(theLocation, Vec3Tools.multiply(aDir, -aViewPnt.distance));
        aViewPnt.direction = aDir;
        aViewPnt.up = anUp;
        this.viewPoint = aViewPnt;
    }
    /** Allow/disallow camera rotation. */
    get toAllowRotation() { return this._myViewMgr.toAllowRotation(this._myViewId); }
    /** Allow/disallow camera rotation. */
    set toAllowRotation(theToAllow) {
        this._myViewMgr.setAllowRotation(this._myViewId, theToAllow);
    }
    /** Get/set if camera up orientation within `AIS_NavigationMode_Orbit` rotation mode should be forced Z up. */
    get toLockOrbitZUp() { return this._myViewMgr.toLockOrbitZUp(this._myViewId); }
    /** Get/set if camera up orientation should be forced Z up. */
    set toLockOrbitZUp(theToLock) {
        this._myViewMgr.setLockOrbitZUp(this._myViewId, theToLock);
    }
    /** Allow/disallow camera panning. */
    get toAllowPanning() { return this._myViewMgr.toAllowPanning(this._myViewId); }
    /** Allow/disallow camera panning. */
    set toAllowPanning(theToAllow) {
        this._myViewMgr.setAllowPanning(this._myViewId, theToAllow);
    }
    /** Allow/disallow camera zooming. */
    get toAllowZooming() { return this._myViewMgr.toAllowZooming(this._myViewId); }
    /** Allow/disallow camera zooming. */
    set toAllowZooming(theToAllow) {
        this._myViewMgr.setAllowZooming(this._myViewId, theToAllow);
    }
    /** Switch perspective/orthographic projection. */
    get isPerspectiveProjection() {
        return this._myViewMgr.isPerspectiveProjection(this._myViewId);
    }
    /** Switch perspective/orthographic projection. */
    set isPerspectiveProjection(theIsPersp) {
        this._myViewMgr.setPerspectiveProjection(this._myViewId, theIsPersp);
    }
    /** Perspective projection FOVy in degrees. */
    get perspectiveProjectionFOVy() {
        return this._myViewMgr.getPerspectiveProjectionFOVy(this._myViewId);
    }
    /** Perspective projection FOVy in degrees. */
    set perspectiveProjectionFOVy(theFovy) {
        this._myViewMgr.setPerspectiveProjectionFOVy(this._myViewId, theFovy);
    }
    /** Get view id */
    get viewId() { return this._myViewId; }
}
;
